import { Plus } from 'lucide-react'
import type { CourseWithoutBlocks } from '@/types/course'
import { useIsCourseAuthor } from '@/features/editor/hooks/useIsCourseAuthor'
import { PendingLessonPill } from './PendingLesson'
import { LessonPill } from './LessonPill'
import { useExpandedLesson } from './useExpandedLesson'
import { LessonExpanded } from './LessonExpanded'
import { SortableItem } from './SortableItem'
import { SortableStrip } from './SortableStrip'
import { useCourseTimeline } from './useCourseTimeline'
import { lockHint } from './lockHint'
import { useMessages } from '@/i18n'
import { ScrollFadeRow } from '@/components/common/ScrollFadeRow'
import { MobileTimelineBar } from './MobileTimelineBar'
import { TimelineCollapseToggle } from './TimelineCollapseToggle'
import { useTimelineCollapsed } from './useTimelineCollapsed'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
}

export const CourseTimeline = ({ course }: Props) => {
  const m = useMessages(messages)
  const isCourseAuthor = useIsCourseAuthor(course)
  const {
    contentLang,
    selectedBlockId,
    selectedBlockIds,
    expandedLessonId,
    isCreatingLesson,
    isGeneratingLessons,
    blocksInRun,
    isBlockWritingRunActive,
    isFrozen,
    selectedRef,
    addLesson,
    handleExpand,
    selectBlockInLesson,
    toggleBlockSelected,
    deleteBlockInLesson,
    reorderLessons,
    onLessonBlockCreated,
  } = useCourseTimeline(course)

  const expandedLesson = useExpandedLesson(course.lessons, expandedLessonId)
  const { collapsed, toggle } = useTimelineCollapsed()

  return (
    <footer
      data-testid="course-timeline"
      className="flex shrink-0 flex-col gap-2.5 bg-surface px-3 pb-3 pt-2 short:gap-1 short:pb-2 short:pt-1"
    >
      <MobileTimelineBar course={course} collapsed={collapsed} onToggleCollapsed={toggle} />
      {/* Сверху — уроки плашками «номер + название», снизу — экраны выбранного урока. */}
      {/* `-mx-2 px-2` — запас под увеличение плашки. Обе ленты — только по X (`overflow-y-hidden`). */}
      <div className="flex items-center gap-2">
        <ScrollFadeRow className="-mx-2 flex h-12 min-w-0 flex-1 items-center gap-2 overflow-y-hidden px-2 py-1 short:h-10">
          <SortableStrip items={course.lessons} onReorder={reorderLessons}>
            {course.lessons.map((lesson, idx) => (
              <SortableItem
                key={lesson.id}
                id={lesson.id}
                disabled={!isCourseAuthor || isFrozen}
                className="flex shrink-0 items-center"
              >
                <LessonPill
                  lesson={lesson}
                  lessonIndex={idx}
                  contentLang={contentLang}
                  isSelected={expandedLessonId === lesson.id}
                  isOpen={Boolean(expandedLessonId === lesson.id && selectedBlockId === null)}
                  onExpand={() => handleExpand(lesson.id)}
                />
              </SortableItem>
            ))}
          </SortableStrip>

          {isCreatingLesson ? (
            <PendingLessonPill
              number={course.lessons.length + 1}
              label={`${contentLang === 'en' ? 'Lesson' : 'Урок'} ${course.lessons.length + 1}`}
            />
          ) : null}

          {isGeneratingLessons ? null : (
            <button
              type="button"
              onClick={addLesson}
              disabled={isCreatingLesson || !isCourseAuthor || isFrozen}
              title={lockHint(isCourseAuthor, isFrozen)}
              aria-label={m.addLesson}
              className="flex h-10 shrink-0 items-center gap-1.5 rounded-full border-2 border-dashed border-muted-foreground/30 px-4 text-sm text-muted-foreground transition hover:border-primary/40 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              {m.addLesson}
            </button>
          )}
        </ScrollFadeRow>
        {/* Вне прокрутки: кнопка стоит на месте, уроки уезжают под неё, а не она за ними.
            Уже `lg` она живёт в баре над уроками (`MobileTimelineBar`). */}
        <div className="hidden lg:flex">
          <TimelineCollapseToggle collapsed={collapsed} onToggle={toggle} />
        </div>
      </div>

      {/* `-mx-1.5` гасит поля ленты (запас под рамку выбранного): блоки и плюс — по краю уроков. */}
      <div
        className={
          collapsed
            ? '-mx-1.5 h-11 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
            : '-mx-1.5 h-[136px] overflow-x-auto overflow-y-hidden short:h-[118px] big:h-[176px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
        }
      >
        {/* Пока новый урок создаётся, текущий остаётся рабочим: сервер может отвечать долго. */}
        {expandedLesson ? (
          <LessonExpanded
            lesson={expandedLesson}
            courseId={course.id}
            contentLang={contentLang}
            selectedBlockId={selectedBlockId}
            selectedRef={selectedRef}
            isCourseAuthor={isCourseAuthor}
            blocksInRun={blocksInRun}
            isBlockWritingRunActive={isBlockWritingRunActive}
            isFrozen={isFrozen}
            onSelectBlock={(id) => selectBlockInLesson(expandedLesson.id, id)}
            onDeleteBlock={(id, next) => deleteBlockInLesson(expandedLesson, id, next)}
            onBlockCreated={(id) => onLessonBlockCreated(expandedLesson.id, id)}
            selectedBlockIds={selectedBlockIds}
            onToggleSelectBlock={toggleBlockSelected}
            compact={collapsed}
          />
        ) : null}
      </div>
    </footer>
  )
}
